import type { ImgHTMLAttributes } from 'react';
import type { Picture as PictureData } from 'vite-imagetools';

type ImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'width' | 'height'>;

interface Props extends ImgProps {
  /** vite-imagetools `as=picture` output: AVIF/WebP sources + raster fallback. */
  image: PictureData;
  /** Required — preserves the alt-text guarantee from #1649. */
  alt: string;
  /**
   * `sizes` attribute applied to the responsive `<source>` variants. Omit for
   * images whose variants are a single density (the browser then falls back to
   * the default `100vw`).
   */
  sizes?: string;
}

/**
 * Renders a vite-imagetools `as=picture` object as a `<picture>` with AVIF and
 * WebP `<source>` variants and a raster `<img>` fallback. The generated variants
 * are resized down to display size, so mobile no longer downloads oversized art.
 *
 * The `<picture>` uses `display: contents` (Tailwind `contents`) so it is
 * transparent to layout — the `<img>` participates in the parent's fl/grid box
 * exactly as a bare `<img>` would. Pass `className`, `loading`,
 * `fetchPriority`, etc. through and they land on the `<img>`.
 */
export default function Picture({ image, alt, sizes, ...imgProps }: Props) {
  return (
    <picture className="contents">
      {Object.entries(image.sources).map(([format, srcSet]) => (
        <source key={format} type={`image/${format}`} srcSet={srcSet} sizes={sizes} />
      ))}
      <img src={image.img.src} width={image.img.w} height={image.img.h} alt={alt} {...imgProps} />
    </picture>
  );
}
